import React, { useState, useEffect } from 'react';
import {
  Bold,
  Underline,
  DollarSign,
  AtSign,
  Heading1,
  Heading3,
  Undo,
  Redo,
  GitBranch,
  Eye,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Minus,
  PenTool,
  List,
  HelpCircle,
} from './icons';
import { CellSelection } from '@tiptap/pm/tables';
import { TableGridSelector } from './TableGridSelector';
import { ListStyleSelector } from './ListStyleSelector';
import { MultilevelListSelector } from './MultilevelListSelector';
import { H2DropdownSelector } from './H2DropdownSelector';
import { PageBreakIcon } from './PageBreakIcon';

/**
 * Selects all cells in the enclosing table so the user can easily
 * change table alignment or format the entire table at once.
 */
export function selectEntireTable(editor) {
  if (!editor) return;
  const { state, view } = editor;
  const { selection } = state;
  const $from = selection.$from;

  let tableNode = null;
  let tablePos = null;

  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (node.type.name === 'table') {
      tableNode = node;
      tablePos = $from.before(d);
      break;
    }
  }

  if (!tableNode && selection.node && selection.node.type.name === 'table') {
    tableNode = selection.node;
    tablePos = selection.from;
  }

  if (!tableNode || tablePos === null) return;

  let firstCellPos = null;
  let lastCellPos = null;

  state.doc.nodesBetween(tablePos, tablePos + tableNode.nodeSize, (node, pos) => {
    if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
      if (firstCellPos === null) firstCellPos = pos;
      lastCellPos = pos;
    }
  });

  if (firstCellPos !== null && lastCellPos !== null) {
    try {
      const sel = CellSelection.create(state.doc, firstCellPos, lastCellPos);
      view.dispatch(state.tr.setSelection(sel));
      editor.commands.focus();
    } catch (err) {
      console.error('Failed to select entire table:', err);
    }
  }
}

/**
 * Isolates the selected statement or sentence into its own block if partially selected,
 * and applies the requested text alignment.
 */
export function alignStatement(editor, alignment) {
  if (!editor) return;
  const { state, view } = editor;
  const { selection } = state;
  const { from, to, empty } = selection;
  const $from = selection.$from;

  // 1. Check if multiple cells or whole table are selected (CellSelection)
  if (selection instanceof CellSelection || typeof selection.forEachCell === 'function') {
    let tr = state.tr;
    const cellsToUpdate = [];
    selection.forEachCell((cellNode, cellPos) => {
      cellsToUpdate.push({ cellNode, cellPos });
    });

    for (const { cellNode, cellPos } of cellsToUpdate) {
      tr = tr.setNodeMarkup(cellPos, undefined, {
        ...cellNode.attrs,
        textAlign: alignment,
      });
      let pPos = cellPos + 1;
      cellNode.forEach((childNode) => {
        if (childNode.type.name === 'paragraph' || childNode.type.name === 'heading') {
          tr = tr.setNodeMarkup(pPos, undefined, {
            ...childNode.attrs,
            textAlign: alignment,
          });
        }
        pPos += childNode.nodeSize;
      });
    }
    view.dispatch(tr);
    return;
  }

  // 2. Check if table node itself is selected (NodeSelection)
  if (selection.node && selection.node.type.name === 'table') {
    let tr = state.tr;
    const tableNode = selection.node;
    const tablePos = selection.from;
    let curPos = tablePos + 1;
    tableNode.forEach((rowNode) => {
      let rCurPos = curPos + 1;
      rowNode.forEach((cNode) => {
        tr = tr.setNodeMarkup(rCurPos, undefined, {
          ...cNode.attrs,
          textAlign: alignment,
        });
        let pPos = rCurPos + 1;
        cNode.forEach((childNode) => {
          if (childNode.type.name === 'paragraph' || childNode.type.name === 'heading') {
            tr = tr.setNodeMarkup(pPos, undefined, {
              ...childNode.attrs,
              textAlign: alignment,
            });
          }
          pPos += childNode.nodeSize;
        });
        rCurPos += cNode.nodeSize;
      });
      curPos += rowNode.nodeSize;
    });
    view.dispatch(tr);
    return;
  }

  // 3. Check if selection / cursor is inside a table
  let tableNode = null;
  let tablePos = null;
  let cellPos = null;
  let currentCellNode = null;

  for (let d = $from.depth; d > 0; d--) {
    const node = $from.node(d);
    if (!cellPos && (node.type.name === 'tableCell' || node.type.name === 'tableHeader')) {
      cellPos = $from.before(d);
      currentCellNode = node;
    }
    if (!tableNode && node.type.name === 'table') {
      tableNode = node;
      tablePos = $from.before(d);
    }
  }

  // If inside a table, apply alignment specifically to the selected column across all rows
  if (tableNode && cellPos !== null) {
    let colIndex = -1;
    let curPos = tablePos + 1;
    tableNode.forEach((rowNode) => {
      let rCurPos = curPos + 1;
      let cIdx = 0;
      rowNode.forEach((cNode) => {
        if (rCurPos === cellPos) {
          colIndex = cIdx;
        }
        rCurPos += cNode.nodeSize;
        cIdx++;
      });
      curPos += rowNode.nodeSize;
    });

    if (colIndex !== -1) {
      const targetAlignment = alignment;

      let tr = state.tr;
      curPos = tablePos + 1;
      tableNode.forEach((rowNode) => {
        let rCurPos = curPos + 1;
        let cIdx = 0;
        rowNode.forEach((cNode) => {
          if (cIdx === colIndex) {
            tr = tr.setNodeMarkup(rCurPos, undefined, {
              ...cNode.attrs,
              textAlign: targetAlignment
            });
            let pPos = rCurPos + 1;
            cNode.forEach((childNode) => {
              if (childNode.type.name === 'paragraph' || childNode.type.name === 'heading') {
                tr = tr.setNodeMarkup(pPos, undefined, {
                  ...childNode.attrs,
                  textAlign: targetAlignment
                });
              }
              pPos += childNode.nodeSize;
            });
          }
          rCurPos += cNode.nodeSize;
          cIdx++;
        });
        curPos += rowNode.nodeSize;
      });
      view.dispatch(tr);
      return;
    }
  }

  if (editor.isActive({ textAlign: alignment })) {
    editor.chain().focus().unsetTextAlign().run();
    return;
  }

  if (empty) {
    editor.chain().focus().setTextAlign(alignment).run();
    return;
  }

  const selFrom = selection.$from;
  const selTo = selection.$to;

  if (selFrom.sameParent(selTo) && selFrom.parent.isTextblock) {
    const parentStart = selFrom.start();
    const parentEnd = selTo.end();
    const isPartialStart = from > parentStart;
    const isPartialEnd = to < parentEnd;

    if (isPartialStart || isPartialEnd) {
      let tr = state.tr;
      if (isPartialEnd) {
        tr = tr.split(to);
      }
      if (isPartialStart) {
        tr = tr.split(from);
      }

      const newFrom = isPartialStart ? from + 2 : from;
      const newTo = Math.min(newFrom + (to - from), tr.doc.content.size);
      tr = tr.setSelection(state.selection.constructor.create(tr.doc, newFrom, newTo));
      view.dispatch(tr);
      editor.chain().focus().setTextAlign(alignment).run();
      return;
    }
  } else if (!selFrom.sameParent(selTo)) {
    let tr = state.tr;
    let currentTo = to;
    let currentFrom = from;

    if (selTo.parentOffset > 0 && selTo.parentOffset < selTo.parent.content.size) {
      tr = tr.split(to);
      currentTo += 2;
    }
    if (selFrom.parentOffset > 0 && selFrom.parentOffset < selFrom.parent.content.size) {
      tr = tr.split(from);
      currentFrom += 2;
      currentTo += 2;
    }
    tr = tr.setSelection(state.selection.constructor.create(tr.doc, currentFrom, currentTo));
    view.dispatch(tr);
  }

  editor.chain().focus().setTextAlign(alignment).run();
}

export function EditorToolbar({
  editor,
  onAddCondition,
  onAddOptional,
  onAddClearSpace,
  onAddDollar,
  onOpenSyntaxHelp,
}) {
  const [, setTick] = useState(0);

  useEffect(() => {
    if (!editor) return;
    const handleUpdate = () => setTick((t) => t + 1);
    editor.on('transaction', handleUpdate);
    editor.on('selectionUpdate', handleUpdate);
    editor.on('focus', handleUpdate);
    editor.on('blur', handleUpdate);
    return () => {
      editor.off('transaction', handleUpdate);
      editor.off('selectionUpdate', handleUpdate);
      editor.off('focus', handleUpdate);
      editor.off('blur', handleUpdate);
    };
  }, [editor]);

  if (!editor) return null;

  const toolbarGroups = [
    {
      id: 'history',
      items: [
        {
          icon: <Undo size={18} />,
          title: 'Undo (Ctrl+Z)',
          action: () => editor.chain().focus().undo().run(),
          isActive: false,
          disabled: !editor.can().undo(),
        },
        {
          icon: <Redo size={18} />,
          title: 'Redo (Ctrl+Y / Ctrl+Shift+Z)',
          action: () => editor.chain().focus().redo().run(),
          isActive: false,
          disabled: !editor.can().redo(),
        },
      ],
    },
    {
      id: 'formatting',
      items: [
        {
          icon: <Bold size={18} />,
          title: 'Bold (Ctrl+B)',
          action: () => editor.chain().focus().toggleBold().run(),
          isActive: editor.isActive('bold'),
          disabled: false,
        },
        {
          icon: <Underline size={18} />,
          title: 'Underline (Ctrl+U)',
          action: () => editor.chain().focus().toggleUnderline().run(),
          isActive: editor.isActive('underline'),
          disabled: false,
        },
      ],
    },
    {
      id: 'headings',
      items: [
        {
          icon: <Heading1 size={18} />,
          title: 'H1: Center',
          action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
          isActive: editor.isActive('heading', { level: 1 }),
          disabled: false,
        },
        {
          component: <H2DropdownSelector key="h2-selector" editor={editor} />,
        },
        {
          icon: <Heading3 size={18} />,
          title: 'H3: Bold + Centre + Underline',
          action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
          isActive: editor.isActive('heading', { level: 3 }),
          disabled: false,
        },
      ],
    },
    {
      id: 'alignment',
      items: [
        {
          icon: <AlignLeft size={18} />,
          title: 'Align Left (<=)',
          action: () => alignStatement(editor, 'left'),
          isActive: editor.isActive({ textAlign: 'left' }),
          disabled: false,
        },
        {
          icon: <AlignCenter size={18} />,
          title: 'Align Center (==)',
          action: () => alignStatement(editor, 'center'),
          isActive: editor.isActive({ textAlign: 'center' }),
          disabled: false,
        },
        {
          icon: <AlignRight size={18} />,
          title: 'Align Right (=>)',
          action: () => alignStatement(editor, 'right'),
          isActive: editor.isActive({ textAlign: 'right' }),
          disabled: false,
        },
        {
          icon: <AlignJustify size={18} />,
          title: 'Align Justify',
          action: () => alignStatement(editor, 'justify'),
          isActive: editor.isActive({ textAlign: 'justify' }),
          disabled: false,
        },
      ],
    },
    {
      id: 'lists',
      items: [
        {
          icon: <List size={18} />,
          title: 'Bullet List',
          action: () => editor.chain().focus().toggleBulletList().run(),
          isActive: editor.isActive('bulletList'),
          disabled: false,
        },
        {
          component: <ListStyleSelector key="list-style-selector" editor={editor} />,
        },
        {
          component: <MultilevelListSelector key="multilevel-selector" editor={editor} />,
        },
      ],
    },
    {
      id: 'line-separators',
      items: [
        {
          icon: <Minus size={18} />,
          title: 'Horizontal Line (<line>)',
          action: () => editor.chain().focus().setHorizontalLine().run(),
          isActive: false,
          disabled: false,
        },
        {
          icon: <PageBreakIcon size={20} />,
          title: 'Page Break (<page break>)',
          action: () => editor.chain().focus().setPageBreak().run(),
          isActive: false,
          disabled: false,
        },
        {
          icon: <PenTool size={18} />,
          title: 'Insert Signature (! 1.0 1.0 signature.png)',
          action: () => editor.chain().focus().insertContent({
            type: 'whiteBlankImage',
            attrs: { width: '1.0', height: '1.0', filename: 'signature.png', align: 'left' }
          }).run(),
          isActive: false,
          disabled: false,
        },
      ],
    },
    {
      id: 'table',
      items: [
        {
          component: <TableGridSelector key="table-grid" editor={editor} />,
        },
      ],
    },
  ];

  return (
    <div className="editor-toolbar" id="editor-toolbar">
      {toolbarGroups.map((group) => (
        <div key={group.id} className="toolbar-group">
          {group.items.map((item, idx) => {
            if (item.component) {
              return item.component;
            }
            return (
              <button
                key={idx}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                className={`toolbar-btn ${item.isActive ? 'is-active' : ''}`}
                onClick={item.action}
                title={item.title}
                disabled={item.disabled}
              >
                {item.icon}
              </button>
            );
          })}
        </div>
      ))}

      <div
        className="toolbar-group toolbar-group-accent"
        style={{ display: 'flex', gap: '8px', alignItems: 'center' }}
      >
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          className="toolbar-btn toolbar-btn-condition"
          onClick={onAddCondition}
          title="Define when a block of content should appear in the generated notice based on field rules"
        >
          <GitBranch size={18} />
          <span>Add Condition</span>
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          className={`toolbar-btn toolbar-btn-condition ${editor.isActive('optionalBlock') ? 'is-active' : ''}`}
          onClick={onAddOptional}
          title="Marks content as optional { } and controls its visibility when data is unavailable"
        >
          <Eye size={18} />
          <span>Make Optional</span>
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          className={`toolbar-btn toolbar-btn-condition ${editor.isActive('clearSpaceBlock') ? 'is-active' : ''}`}
          onClick={onAddClearSpace}
          title="Removes unnecessary blank spaces or commas @...@ when optional content is not available"
        >
          <AtSign size={18} />
        </button>

        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          className={`toolbar-btn toolbar-btn-condition ${editor.isActive('dollarBlock') ? 'is-active' : ''}`}
          onClick={onAddDollar}
          title="Removes the entire table row when all variables in the row are empty"
        >
          <DollarSign size={18} />
          <span>Skip Row</span>
        </button>
      </div>

      {onOpenSyntaxHelp && (
        <div
          className="toolbar-group toolbar-group-help"
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', flexShrink: 0 }}
        >
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            className="syntax-help-custom-btn"
            onClick={onOpenSyntaxHelp}
            title="Open Syntax Help and documentation guide"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '6px 14px',
              height: '32px',
              width: 'auto',
              minWidth: 'max-content',
              whiteSpace: 'nowrap',
              borderRadius: '8px',
              background: '#ffffff',
              color: '#2563eb',
              border: '1.5px solid #2563eb',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              boxShadow: '0 1px 2px rgba(37, 99, 235, 0.05)',
              flexShrink: 0,
            }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#eff6ff')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#ffffff')}
          >
            <HelpCircle size={15} color="#2563eb" style={{ flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap', display: 'inline-block' }}>Syntax Help</span>
          </button>
        </div>
      )}
    </div>
  );
}
